/**
 * Similarity calculations for embeddings
 */

/**
 * Calculate cosine similarity between two vectors
 * Returns a value between -1 and 1, where 1 means identical direction
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) {
    throw new Error(`Vector length mismatch: ${a.length} vs ${b.length}`)
  }
  
  if (a.length === 0) return 0
  
  let dotProduct = 0
  let normA = 0
  let normB = 0
  
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i]
    normA += a[i] * a[i]
    normB += b[i] * b[i]
  }
  
  if (normA === 0 || normB === 0) return 0
  
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB))
}

/**
 * Calculate pairwise similarities between all observations
 * Returns an array of [index1, index2, similarity] tuples above threshold
 */
export function calculateSimilarities(
  embeddings: number[][],
  threshold: number = 0.7
): Array<[number, number, number]> {
  const similarities: Array<[number, number, number]> = []
  
  for (let i = 0; i < embeddings.length; i++) {
    for (let j = i + 1; j < embeddings.length; j++) {
      const sim = cosineSimilarity(embeddings[i], embeddings[j])
      if (sim >= threshold) {
        similarities.push([i, j, sim])
      }
    }
  }
  
  return similarities
}

/**
 * Build adjacency list from similarities
 */
export function buildAdjacencyList(
  similarities: Array<[number, number, number]>,
  nodeCount: number
): Map<number, Array<{ index: number; similarity: number }>> {
  const adjacency = new Map<number, Array<{ index: number; similarity: number }>>()
  
  for (let i = 0; i < nodeCount; i++) {
    adjacency.set(i, [])
  }
  
  for (const [i, j, sim] of similarities) {
    adjacency.get(i)?.push({ index: j, similarity: sim })
    adjacency.get(j)?.push({ index: i, similarity: sim })
  }
  
  return adjacency
}

/**
 * Calculate importance score based on connectivity
 * Nodes with more high-similarity connections are more important
 */
export function calculateImportance(
  adjacency: Map<number, Array<{ index: number; similarity: number }>>,
  nodeCount: number
): number[] {
  const importance: number[] = new Array(nodeCount).fill(1)
  
  for (let i = 0; i < nodeCount; i++) {
    const connections = adjacency.get(i) || []
    // Importance is base 1 plus sum of similarities
    importance[i] = 1 + connections.reduce((sum, conn) => sum + conn.similarity, 0)
  }
  
  // Normalize to range [1, 3]
  const maxImportance = Math.max(...importance)
  const minImportance = Math.min(...importance)
  const range = maxImportance - minImportance || 1
  
  return importance.map(imp => 1 + 2 * (imp - minImportance) / range)
}
